import React, { useEffect, memo, isValidElement, useState, useCallback, useMemo } from "react";
import { Flex, Text, View, Avatar, Link } from "@smarterservices/smarter-ui";
import BlockRenderer from '@shared/components/BlockKit/BlockRenderer';
import { BlockKitProvider } from '@shared/components/BlockKit/BlockRenderer/context/BlockKitContext';
import { parseMessage } from '../../utils/messageParser';

/**
 * ChatMessage component renders an individual message in the chat.
 * This component is memoized to prevent unnecessary re-renders.
 * 
 * Supports both plain text and BlockKit formatted messages:
 * - Plain text: Rendered as-is with text wrapping
 * - BlockKit JSON: Automatically detected and rendered with BlockRenderer
 * 
 * @param {object} props - Component properties
 * @param {object} props.msg - Message object containing text, sender info, and timestamps
 * @param {string} props.msg.message.text - Message text (plain text or BlockKit JSON string)
 * @param {function} props.formatTimestamp - Function to format message timestamps
 * @param {string} props.identity - Current user's identity
 * @param {boolean} props.isHighlighted - Whether this message should be highlighted
 * @param {function} props.registerRef - Function to register this message's DOM ref
 * @param {function} props.renderSessionTime - Optional custom renderer for session time
 * @returns {JSX.Element}
 * 
 * @example
 * // Plain text message
 * <ChatMessage msg={{ message: { text: "Hello world" }, displayName: "John" }} />
 * 
 * @example
 * // BlockKit message
 * <ChatMessage msg={{ 
 *   message: { 
 *     text: '[{"type":"section","text":{"type":"plain_text","text":"Hello from BlockKit"}}]' 
 *   }, 
 *   displayName: "John" 
 * }} />
 */
const ChatMessage = memo(({
  msg,
  formatTimestamp,
  identity,
  isHighlighted,
  registerRef,
  renderSessionTime,
}) => {
  /**
   * Parse BlockKit JSON and memoize the result
   * Prevents expensive JSON parsing on every render
   */
  const blockKitData = useMemo(() => {
    if (!msg.message.text) {
      return { isBlockKit: false, blocks: null, error: null };
    }

    try {
      // Try to parse as JSON
      const parsed = JSON.parse(msg.message.text);

      // Check if it's a valid BlockKit structure (array or object with type property)
      if (Array.isArray(parsed) || (typeof parsed === 'object' && parsed.type)) {
        return {
          isBlockKit: true,
          blocks: Array.isArray(parsed) ? parsed : [parsed],
          error: null
        };
      }
      return { isBlockKit: false, blocks: null, error: null };
    } catch (e) {
      // Not JSON, treat as plain text
      return { isBlockKit: false, blocks: null, error: null };
    }
  }, [msg.message.text]);

  const { isBlockKit, blocks: blockKitBlocks, error: blockKitError } = blockKitData;

  /**
   * Render message content - either BlockKit or plain text
   * @returns {JSX.Element} Rendered message content
   */
  const renderMessageContent = () => {
    if (isBlockKit && blockKitBlocks) {
      return (
        <View as="div" margin="x-small 0 0 0">
          <BlockKitProvider replacements={{}}>
            <BlockRenderer blocks={blockKitBlocks} />
          </BlockKitProvider>
        </View>
      );
    }

    if (blockKitError) {
      return (
        <View as="div" margin="x-small 0 0 0">
          <Text variant="contentSmall" color="danger">
            Error rendering BlockKit: {blockKitError}
          </Text>
        </View>
      );
    }

    // Plain text message - parse for URLs and render with clickable links
    // Memoize to prevent re-parsing on every render
    const parsedContent = useMemo(() => parseMessage(msg.message.text), [msg.message.text]);

    return (
      <Text
        wrap="break-word"
        variant="contentSmall"
      >
        {parsedContent.map((segment, index) => {
          if (segment.type === 'link') {
            return (
              <Link
                key={index}
                href={segment.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {segment.display}
              </Link>
            );
          }
          return <span key={index}>{segment.content}</span>;
        })}
      </Text>
    );
  };
  /**
   * Renders the session time for this message
   * Handles three cases:
   * 1. No session time available: returns null
   * 2. Custom renderer provided: uses that and wraps primitive results in Text
   * 3. Default case: shows formatted timestamp
   * 
   * @returns {JSX.Element|null} - Rendered session time or null
   */
  const renderMessageSessionTime = () => {
    if (msg.sessionTime === undefined) {
      return null;
    }

    if (renderSessionTime && typeof renderSessionTime === 'function') {
      const renderedTime = renderSessionTime(msg.sessionTime, msg);

      // Check if the result is a React element
      if (isValidElement(renderedTime)) {
        return renderedTime;
      } else {
        // If it's just text or another primitive, wrap it in a Text component
        return (
          <Text as="span" variant="legend" color="secondary">
            {renderedTime}
          </Text>
        );
      }
    }

    return (
      <Text as="span" variant="legend" color="secondary">
        {formatTimestamp(msg.createdDate)}
      </Text>
    );
  };

  return (
    <View as="div"
      borderColor={isHighlighted ? "info" : "primary"}
      borderWidth={isHighlighted ? "small" : "small"}
      borderRadius={isHighlighted ? "medium" : "medium"}
      background="primary"
      margin="0 0 xxx-small 0"
      themeOverride={{
        borderColorPrimary: '#eee'
      }}

    >
      <Flex
        direction="row"
        alignItems="start"
        padding="x-small"
        width="100%"
        gap="small"
        elementRef={registerRef}
      >
        <Flex.Item>
          <Avatar
            name={msg.displayName}
            size="x-small"
            color={msg.identity === identity ? "default" : "ash"}
          />
        </Flex.Item>
        <Flex.Item shouldGrow size="0">
          <Flex direction="column" alignItems="start">
            <Flex direction="row" alignItems="center" gap="x-small">
              <Text as="span" wrap="break-word" variant="contentSmall">
                <strong>{msg.displayName}</strong>
              </Text>
              {renderMessageSessionTime()}
            </Flex>
            {renderMessageContent()}
          </Flex>
        </Flex.Item>
      </Flex>
    </View>
  );
});

// Add display name for debugging
ChatMessage.displayName = 'ChatMessage';

/**
 * MessageList component renders the list of chat messages and "New Message" indicator.
 * It handles message highlighting, ref registration for scrolling, and custom time formatting.
 * 
 * Messages support both plain text and BlockKit JSON format:
 * - Plain text messages are rendered as standard text
 * - BlockKit JSON messages are automatically detected and rendered with rich formatting
 *
 * @param {object} props
 * @param {Array} props.messages - Array of chat message objects (supports plain text or BlockKit JSON)
 * @param {object} props.messagesEndRef - Ref to bottom anchor element.
 * @param {function} props.formatTimestamp - Formats message timestamp.
 * @param {boolean} props.showNewMessageButton - Whether to show "New Message" tag.
 * @param {function} props.scrollToBottom - Function to scroll to bottom.
 * @param {string} props.identity - Current user's identity.
 * @param {object} props.messageRefs - Refs object for message elements.
 * @param {string} props.highlightedMessageId - ID of message to highlight.
 * @param {function} [props.renderSessionTime] - Optional custom renderer for session time.
 * @returns {JSX.Element}
 */
export default function MessageList({
  messages,
  messagesEndRef,
  formatTimestamp,
  identity,
  messageRefs = {},
  highlightedMessageId = null,
  renderSessionTime = null
}) {
  /**
   * Creates a ref registration function for a specific message
   * Memoized to prevent ChatMessage components from re-rendering unnecessarily
   * 
   * @param {object} msg - Message object
   * @returns {function} - Function to register the element ref
   */
  const registerMessageRef = useCallback((msg) => (element) => {
    if (element && msg.sid) {
      messageRefs.current[msg.sid] = element;
    }
  }, [messageRefs]);

  return (
    <>
      {messages.map((msg, index) => (
        <ChatMessage
          key={msg.sid || `msg-${index}-${msg.createdDate}`}
          msg={msg}
          formatTimestamp={formatTimestamp}
          identity={identity}
          isHighlighted={highlightedMessageId === msg.sid}
          registerRef={registerMessageRef(msg)}
          renderSessionTime={renderSessionTime}
        />
      ))}
      <div ref={messagesEndRef} />

    </>
  );
}
